import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-custom-radio',
  templateUrl: './custom-radio.component.html',
  styleUrls: ['./custom-radio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomRadioComponent),
      multi: true,
    },
  ],
  host: {
    class: 'app-custom-radio',
    '[class.app-radio-checked]': 'checked',
    '[class.app-radio-disabled]': 'disabled',
    '[class.app-radio-non-interactive]': '!interactive',
    '[class.app-radio-primary]': 'color === "primary"',
    '[class.app-radio-accent]': 'color === "accent"',
    '[class.app-radio-warn]': 'color === "warn"',
    '[class.app-radio-align-top]': 'alignTop',
    '(click)': 'interactive && _onInputClick($event)',
  },
})
export class CustomRadioComponent implements ControlValueAccessor {
  @Input() checked = false;
  @Input() disabled = false;
  @Input() interactive = true;
  @Input() color: 'primary' | 'accent' | 'warn' = 'primary';
  @Input() value: any;
  @Input() name: string | null = null;
  @Input() id: string = `app-radio-${Math.random().toString(36).substring(2)}`;
  @Input() required: boolean = false;
  @Input() ariaLabel: string | null = null;
  @Input() ariaLabelledby: string | null = null;
  @Input() tabIndex: number = 0;
  @Input() alignTop = false;

  @Output() change = new EventEmitter<any>();

  // Animation state
  public rippleActive = false;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  public _onInputClick(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.disabled || !this.interactive || this.checked) {
      return;
    }

    this.checked = true;
    this.onChange(this.value);
    this.change.emit(this.value);

    // Trigger ripple effect
    this.rippleActive = true;
    setTimeout(() => {
      this.rippleActive = false;
      this.cdr.markForCheck();
    }, 300);
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    this.checked = this.value === value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }
}
